import {
  EUserType,
  IAvaliacaoDiariaQuestionario,
  IAvaQuestionario,
  IProfessor,
  IQuestionario,
} from "@deltasge/marauders-map";
import { snack } from "components/GlobalSnackbar";
import { api, Environment, history } from "configs";
import { format, set } from "date-fns";
import { useFilter } from "hooks/useFilter";
import { IFilterDiarioQuestionario } from "interfaces";
import { Main } from "pages/_layouts/Main";
import React, { FC, useEffect, useState } from "react";
import { getError } from "utils";
import { Filter as FilterIcon } from "mdi-material-ui";
import { Filter } from "./components/Filter";
import { EmptyState } from "components/EmptyStates";
import {
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { useAppSelector } from "store";
import { useLocation } from "react-router-dom";
import { Backdrop } from "components/Backdrop";

import { CardAvaliacao } from "./components/CardAvaliacao";
import { useConfirmDialog } from "hooks/useDialogConfirm";
import { useDialog } from "hooks/useDialog";
import { getProfessoresTreeView } from "@deltasge/marauders-map/lib/utils";

export const QuestionarioDiario: FC = () => {
  const [loading, setLoading] = useState(false);
  const [loadingQuestionarios, setLoadingQuestionarios] = useState(false);
  const [creating, setCreating] = useState(false);
  const [questionarios, setQuestionarios] = useState<IQuestionario[]>([]);
  const [avaliacoes, setAvaliacoes] = useState<IAvaliacaoDiariaQuestionario[]>(
    []
  );
  const [professresDaTurma, setProfessresDaTurma] = useState<
    Partial<IProfessor>[]
  >([]);
  const [avaliacao, setAvaliacao] = useState<{
    avaliacao: IAvaliacaoDiariaQuestionario;
    index: number;
  }>();

  const { pathname, search } = useLocation();

  const {
    usuario: { usuario },
    tamanhoPapeis: { data: tamanhoPapeis },
    configPapelPadrao,
    treeView: { professores },
  } = useAppSelector((state) => state);

  const { RenderDialog: DialogDelete, show: handleDelete } =
    useConfirmDialog<IAvaliacaoDiariaQuestionario>({
      defaults: {
        title: "Remover avaliação",
        content: "Deseja mesmo remover esta avaliação e suas respostas?",
      },
      onConfirmed: async (payload, hide) => {
        try {
          await api.delete(`avaliacao-questionario/${payload.idAvaliacao}`);
          handleFilter();
        } catch (error) {
          snack.error(getError(error));
        } finally {
          hide();
        }
      },
    });

  const {
    RenderDialog: DialogProfessores,
    hide: hideProfessorDialog,
    show: showProfessorDialog,
  } = useDialog({
    title: "Selecione o professor",
    options: {
      maxWidth: "sm",
      modal: true,
      scroll: "paper",
    },
  });

  const handleFilterCurso = (idCurso?: number) =>
    navigate({
      ...filter,
      idCurso,
      idSerie: undefined,
      idTurma: undefined,
      idMateria: undefined,
    });

  const handleFilterSerie = (idSerie?: number) =>
    navigate({
      ...filter,
      idSerie,
      idTurma: undefined,
      idMateria: undefined,
    });

  const handleFilterTurma = (idTurma?: number) =>
    navigate({
      ...filter,
      idTurma,
      idMateria: undefined,
    });

  const handleFilterData = (data?: Date) => {
    if (!data) return;
    navigate({
      ...filter,
      data: set(data, { hours: 12, minutes: 0, seconds: 0 }),
    });
  };

  const handleFilter = async () => {
    try {
      const { data, idTurma, idQuestionario } = filter;
      if (idTurma && data && idQuestionario) {
        setLoading(true);

        const { data: avaliacoes } = await api.get<
          IAvaliacaoDiariaQuestionario[]
        >(
          `avaliacao-questionario/diario/${idQuestionario}/turma/${idTurma}/data/${format(
            data,
            "yyyy-MM-dd"
          )}`,
          {
            cache: { ignoreCache: true },
          }
        );
        if (Array.isArray(avaliacoes)) setAvaliacoes(avaliacoes);
      } else {
        setAvaliacoes([]);
      }
    } catch (err) {
      snack.warning(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (
    avaliacao: IAvaliacaoDiariaQuestionario,
    index: number
  ) => {
    if (avaliacao.idAvaliacao) {
      history.push(`/questionario/responder/${avaliacao.idAvaliacao}`, {
        pathname,
        search,
      });
    } else if (usuario?.tipoUsuario == EUserType.Professor && usuario.id) {
      await handleCreate(avaliacao, index, usuario.id);
    } else {
      showProfessorDialog();
      setAvaliacao({ avaliacao, index });
    }
  };

  const handleCreate = async (
    avaliacao: IAvaliacaoDiariaQuestionario,
    index: number,
    idProfessor: number
  ) => {
    try {
      setCreating(true);
      if (
        !filter.idCurso ||
        !filter.idSerie ||
        !filter.idTurma ||
        !filter.idQuestionario ||
        !filter.data
      ) {
        throw Error(
          "Para criar uma avaliação você precisa selecionar uma data, curso, serie, turma e questionário"
        );
      }
      if (!avaliacao.etapa) {
        throw Error("A etapa do curso e data selecionada não foi encontrada");
      }
      const { data } = await api.post<IAvaQuestionario>(
        "avaliacao-questionario",
        {
          bimestre: avaliacao.etapa,
          data: filter.data,
          idAluno: avaliacao.idAluno,
          idCurso: filter.idCurso,
          idSerie: filter.idSerie,
          idTurma: filter.idTurma,
          idQuestionario: filter.idQuestionario,
          idProfessor,
          nota: null,
        }
      );
      avaliacao.idAvaliacao = data.id;
      avaliacao.data = new Date();
      avaliacoes[index] = avaliacao;
      setAvaliacoes(avaliacoes);
      history.push(`/questionario/responder/${data.id}`, {
        pathname,
        search,
      });
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setCreating(false);
    }
  };

  const handleVisualizar = (avaliacao: IAvaliacaoDiariaQuestionario) => {
    const url = `${Environment.RELATORIO_API_URL}/api/${usuario?.escolaId}/questionario/${avaliacao.idAvaliacao}/impressao/`;
    const papel =
      Object.keys(configPapelPadrao).length > 0
        ? configPapelPadrao
        : tamanhoPapeis.find((f) => f.name == "A4");
    const params: Record<string, string> = {
      authorization: `Bearer ${usuario?.token}`,
    };
    if (papel) {
      params.width = `${
        papel.height > papel.width ? papel.width : papel.height
      }mm`;
      params.height = `${
        papel.height > papel.width ? papel.height : papel.width
      }mm`;
      params.marginTop = `${papel.marginTop}mm`;
      params.marginBottom = `${papel.marginBottom}mm`;
      params.marginLeft = `${papel.marginLeft}mm`;
      params.marginRight = `${papel.marginBottom}mm`;
    }
    const query = new URLSearchParams(params);
    window.open(url.concat("?").concat(query.toString()), "_blank");
  };

  const handleAvaliacoes = (avaliacao: IAvaliacaoDiariaQuestionario) => {
    try {
      if (
        !filter.idCurso ||
        !filter.idSerie ||
        !filter.idTurma ||
        !filter.idQuestionario
      ) {
        throw new Error(
          "Para ver as avaliações do aluno você precisa selecionar um, curso, serie, turma e questionário"
        );
      }
      const params = {
        idCurso: filter.idCurso.toString(),
        idSerie: filter.idSerie.toString(),
        idTurma: filter.idTurma.toString(),
        idQuestionario: filter.idQuestionario.toString(),
        idAluno: avaliacao.idAluno.toString(),
      };
      const queryString = new URLSearchParams(params).toString();
      history.push(`/questionario/avaliacoes/?${queryString}`, {
        pathname,
        search,
      });
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const {
    Component,
    navigate,
    filter,
    selecionados: { turma },
  } = useFilter<IFilterDiarioQuestionario>({
    pathname: "/questionario/diarias/",
    queryKeys: ["idCurso", "idSerie", "idTurma", "data", "idQuestionario"],
    initialValue: {
      data: new Date(),
    },
    options: {
      curso: { onClick: handleFilterCurso, mostrarTodos: true },
      serie: { onClick: handleFilterSerie, mostrarTodos: true },
      turma: { onClick: handleFilterTurma, mostrarTodos: true },
      data: { onClick: handleFilterData, allowsToClean: false },
    },
  });

  const getQuestionarios = async () => {
    try {
      setLoadingQuestionarios(true);
      const { data } = await api.get<[IQuestionario[], number]>(
        "questionario",
        {
          params: {
            where: { anoLetivo: filter.anoLetivo, diario: 1 },
            select: [
              "id",
              "nome",
              "diario",
              "multiBimestre",
              "idSerie",
              "idCurso",
            ],
            order: { nome: "ASC" },
          },
        }
      );
      if (Array.isArray(data) && Array.isArray(data[0])) {
        setQuestionarios(data[0]);
      }
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setLoadingQuestionarios(false);
    }
  };

  useEffect(() => {
    getQuestionarios();
  }, [filter.anoLetivo]);

  useEffect(() => {
    handleFilter();
    if (filter.idTurma && turma) {
      const professoresDaTurma = getProfessoresTreeView({ turma, professores });
      setProfessresDaTurma(professoresDaTurma);
    }
  }, [filter.data, filter.idTurma, turma]);

  return (
    <Main
      title="Diária"
      loading={loading || loadingQuestionarios}
      rightbarIcon={<FilterIcon />}
      rightbarChildren={
        <Filter
          Component={Component}
          filter={filter}
          navigate={navigate}
          questionarios={questionarios}
        />
      }
    >
      <DialogProfessores>
        {avaliacao && (
          <List>
            {professresDaTurma.map((professor, i) => (
              <ListItem key={`professor-${professor.id}-${i}`}>
                <ListItemButton
                  onClick={() => {
                    if (professor.id) {
                      hideProfessorDialog();
                      handleCreate(
                        avaliacao.avaliacao,
                        avaliacao.index,
                        professor.id
                      );
                    }
                  }}
                >
                  <ListItemText primary={professor.nome} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogProfessores>
      <DialogDelete />
      {avaliacoes.length > 0 && (
        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="stretch"
        >
          {avaliacoes.map((avaliacao, index) => (
            <CardAvaliacao
              avaliacao={avaliacao}
              key={`avaliacao-${avaliacao.idAluno}-${index}`}
              handleClick={(avaliacao) => handleClick(avaliacao, index)}
              handleDelete={handleDelete}
              handleAvaliacoes={handleAvaliacoes}
              handleVisualizar={handleVisualizar}
            />
          ))}
        </Grid>
      )}
      {(!filter.idCurso ||
        !filter.idSerie ||
        !filter.idTurma ||
        !filter.idQuestionario) &&
        !loading && (
          <EmptyState
            type="search"
            title="Selecione no filtro"
            subtitle="Selecione ao menos uma turma e questionário para apresentar os alunos"
          />
        )}
      <Backdrop open={creating} message="Criando avaliação" />
    </Main>
  );
};
